// # Subscription Settings Tab Component
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuthStore } from '../../../auth/state';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../auth/firebase';
import './subscription_settings.css';

// # Define subscription types matching the Firestore structure
const SUBSCRIPTION_TYPES = {
  STEP1_LIBRARY: {
    id: 'step1_library',
    name: 'USMLE Step 1 Library',
    requiresMentor: false
  },
  STEP2_LIBRARY: {
    id: 'step2_library',
    name: 'USMLE Step 2 Library',
    requiresMentor: false
  },
  STEP1_MENTORSHIP: {
    id: 'step1_mentorship',
    name: 'USMLE Step 1 Mentorship',
    requiresMentor: true
  },
  STEP2_MENTORSHIP: {
    id: 'step2_mentorship',
    name: 'USMLE Step 2 Mentorship',
    requiresMentor: true
  },
  // # Add OET subscription type
  OET: {
    id: 'OET',
    name: 'Occupation English Test',
    requiresMentor: true
  },
  // # Add US_VISA_Counselling subscription type
  US_VISA_COUNSELLING: {
    id: 'US_VISA_Counselling',
    name: 'US VISA Counselling',
    requiresMentor: true
  }
};

function SubscriptionSettings({ isDarkMode }) {
  const { user } = useAuthStore();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // # Fetch subscription data
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        if (!user?.uid) {
          setError('User not found');
          return;
        }

        console.log('Loading subscription data for user:', user.uid);
        const startTime = performance.now();

        const subscriptionRef = doc(db, 'subscriptions', user.uid);
        const docSnap = await getDoc(subscriptionRef);
        
        const endTime = performance.now();
        console.log('Subscription data Firestore read:', {
          userId: user.uid,
          exists: docSnap.exists(),
          readTime: `${(endTime - startTime).toFixed(2)}ms`,
          timestamp: new Date().toISOString()
        });

        if (docSnap.exists()) {
          setSubscriptionData(docSnap.data());
        } else {
          // # Set default subscription structure if none exists
          setSubscriptionData({
            subscriptions: {
              step1_library: {
                status: 'inactive',
                startDate: 'none',
                endDate: 'none'
              },
              step2_library: {
                status: 'inactive',
                startDate: 'none',
                endDate: 'none'
              },
              step1_mentorship: {
                status: 'inactive',
                startDate: 'none',
                endDate: 'none',
                mentor: 'none'
              },
              step2_mentorship: {
                status: 'inactive',
                startDate: 'none',
                endDate: 'none',
                mentor: 'none'
              },
              OET: {
                status: 'inactive',
                startDate: 'none',
                endDate: 'none',
                mentor: 'none'
              },
              US_VISA_Counselling: {
                status: 'inactive',
                startDate: 'none',
                endDate: 'none',
                mentor: 'none'
              }
            }
          });
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError('Failed to load subscription information');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user?.uid]);

  // # Format date for display
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'none') return 'Not Available';
    return new Date(dateString).toLocaleDateString();
  };

  // # Get subscription status class
  const getStatusClass = (status) => {
    return `sub_st_status-badge sub_st_status-${status || 'inactive'}`;
  };

  // # Get subscription features based on type
  const getSubscriptionFeatures = (type) => {
    switch (type) {
      case 'step1_library':
        return [
          'Full access to USMLE Step 1 Library',
          'Regular content updates'
        ];
      case 'step2_library':
        return [
          'Full access to USMLE Step 2 Library',
          'Regular content updates'
        ];
      case 'step1_mentorship':
        return [
          'All Step 1 Library features',
          'One-on-one mentorship',
          'Personalized study plan',
          'Regular progress reviews'
        ];
      case 'step2_mentorship':
        return [
          'All Step 2 Library features',
          'One-on-one mentorship',
          'Personalized study plan',
          'Regular progress reviews'
        ];
      case 'OET':
        return [
          'Full access to OET preparation materials',
          'One-on-one OET mentorship',
          'Personalized study plan'
        ];
      case 'US_VISA_Counselling':
        return [
          'US VISA application guidance',
          'Document preparation assistance',
          'One-on-one counselling sessions',
          'Interview preparation'
        ];
      default:
        return ['No features available'];
    }
  };

  // # Format mentor display
  const formatMentorDisplay = (mentor) => {
    if (!mentor || mentor === 'none') return 'Not Assigned';
    // Handle both string and object mentor formats
    if (typeof mentor === 'string') return mentor;
    // If mentor is an object, format the display
    return mentor.name || `${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || mentor.email || 'Not Available';
  };

  return (
    <div className="sub_st_section">
      <div className="sub_st_header">
        <h2>Subscription Management</h2>
      </div>

      {loading ? (
        <div className="sub_st_loading-state">Loading subscription details...</div>
      ) : error ? (
        <div className="sub_st_error-message">{error}</div>
      ) : (
        <div className="sub_st_subscription-grid">
          {Object.entries(SUBSCRIPTION_TYPES).map(([key, type]) => {
            const subscription = subscriptionData?.subscriptions?.[type.id] || {
              status: 'inactive',
              startDate: 'none',
              endDate: 'none',
              ...(type.requiresMentor && { mentor: 'none' })
            };

            return (
              <div key={type.id} className="sub_st_subscription-card">
                <div className="sub_st_subscription-header">
                  <h4>{type.name}</h4>
                  <span className={getStatusClass(subscription.status)}>
                    {subscription.status?.charAt(0).toUpperCase() + subscription.status?.slice(1)}
                  </span>
                </div>

                <div className="sub_st_subscription-details">
                  <div className="sub_st_detail-row">
                    <span>Start Date:</span>
                    <span>{formatDate(subscription.startDate)}</span>
                  </div>
                  <div className="sub_st_detail-row">
                    <span>End Date:</span>
                    <span>{formatDate(subscription.endDate)}</span>
                  </div>
                  {type.requiresMentor && (
                    <div className="sub_st_detail-row">
                      <span>Mentor:</span>
                      <span>{formatMentorDisplay(subscription.mentor)}</span>
                    </div>
                  )}
                </div>

                <div className="sub_st_subscription-features">
                  <h5>Features</h5>
                  <ul>
                    {getSubscriptionFeatures(type.id).map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>

                <div className="sub_st_subscription-actions">
                  {subscription.status === 'inactive' ? (
                    <button 
                      className="sub_st_browse-plans-btn"
                      onClick={() => window.location.href = '/products'}
                    >
                      Get {type.name}
                    </button>
                  ) : (
                    <button 
                      className="sub_st_manage-btn"
                      onClick={() => window.location.href = '/dashboard/billing'}
                    >
                      Manage Subscription
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

SubscriptionSettings.propTypes = {
  isDarkMode: PropTypes.bool
};

SubscriptionSettings.defaultProps = {
  isDarkMode: false
};

export default SubscriptionSettings; 